import { date } from "quasar";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";
import { CURRENCY } from "./constants";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
} from "date-fns";
import { offline } from "src/boot/offline";
import { TYPE_LABEL_MAP } from "src/assets/scripts/utilities/routeGenerator";

const { formatDate, getDateDiff, isSameDate } = date;

export const extractPincodeFromAddress = (address) => {
  const pincode = address.match(/ [1-9]\d{2}\s?\d{3}/g);

  return pincode ? pincode[0].trim() : "";
};

export const removeGstinFromAddress = (address) => {
  address = address?.trim();

  let addressLines = address?.split("\n");

  addressLines = addressLines?.map((addressLine) => {
    addressLine = addressLine?.replace(/GSTIN:?/gi, "");

    addressLine = addressLine?.replace(
      /\d{2}[A-Z]{5}\d{4}[A-Z][A-Z\d]Z[A-Z\d]/g,
      "",
    );

    addressLine = addressLine?.replace(/\bURP-\d+\b/g, "");

    return addressLine?.trim();
  });

  addressLines = addressLines?.filter((addressLine) => addressLine?.length);

  address = addressLines?.join("\n");

  return address?.trim();
};

export const dayAgoByDay = (date) => {
  if (getDateDiff(new Date(), date, "date") < 1) {
    return "Today";
  } else if (getDateDiff(new Date(), date, "date") < 2) {
    return "Yesterday";
  } else {
    return formatDate(date, "DD/MM/YY");
  }
};

/**
 * Adjusts a number to the specified digit.
 *
 * @param {"round" | "floor" | "ceil"} type The type of adjustment.
 * @param {number} value The number.
 * @param {number} exp The exponent (the 10 logarithm of the adjustment base).
 * @returns {number} The adjusted value.
 */
export function decimalAdjust(type, value, exp) {
  type = String(type);

  if (!["round", "floor", "ceil"].includes(type)) {
    throw new TypeError(
      "The type of decimal adjustment must be one of 'round', 'floor', or 'ceil'.",
    );
  }

  exp = Number(exp);

  value = Number(value);

  if (exp % 1 !== 0 || Number.isNaN(value)) {
    return NaN;
  } else if (exp === 0) {
    return Math[type](value);
  }

  const [magnitude, exponent = 0] = value.toString().split("e");

  const adjustedValue = Math[type](`${magnitude}e${exponent - exp}`);

  // Shift back
  const [newMagnitude, newExponent = 0] = adjustedValue.toString().split("e");

  return Number(`${newMagnitude}e${+newExponent + exp}`);
}

export function roundOff(value, place = 3) {
  return decimalAdjust("round", value, -1 * place);
}

export function floorOff(value, place = 3) {
  return decimalAdjust("floor", value, -1 * place);
}

// ref: https://blog.logrocket.com/programmatic-file-downloads-in-the-browser-9a5186298d5c/
export function downloadBlob(blob, filename, view = false) {
  // Create an object URL for the blob object
  const url = URL.createObjectURL(blob);

  // Create a new anchor element
  const a = document.createElement("a");

  // Set the href and download attributes for the anchor element
  // You can optionally set other attributes like `title`, etc
  // Especially, if the anchor element will be attached to the DOM
  a.href = url;

  if (view) {
    a.target = "_blank";
  } else {
    a.download = filename || "Document";
  }

  // Click handler that releases the object URL after the element has been clicked
  // This is required for one-off downloads of the blob content
  function clickHandler() {
    setTimeout(() => {
      URL.revokeObjectURL(url);

      this.removeEventListener("click", clickHandler);
    }, 150);
  }

  // Add the click event listener on the anchor element
  // Comment out this line if you don't want a one-off download of the blob content
  a.addEventListener("click", clickHandler, false);

  // Programmatically trigger a click on the anchor element
  // Useful if you want the download to happen automatically
  // Without attaching the anchor element to the DOM
  // Comment out this line if you don't want an automatic download of the blob content
  a.click();

  // Return the anchor element
  // Useful if you want a reference to the element
  // in order to attach it to the DOM or use it in some other way
  return a;
}

export const findIndex = (array, item, itemProperty) => {
  return unref(array)?.findIndex((i) => {
    if (itemProperty) {
      return i[itemProperty] === unref(item)[itemProperty];
    } else {
      return i === item;
    }
  });
};

export const isEmpty = (obj) => {
  return (
    [Object, Array].includes((obj || {}).constructor) &&
    !Object.entries(obj || {}).length
  );
};

export const numberFormat = ({
  currency = CURRENCY,
  mfd = 3,
  number,
  style = "decimal",
} = {}) => {
  return new Intl.NumberFormat("en-IN", {
    currency: currency.code,
    maximumFractionDigits: mfd,
    minimumFractionDigits: mfd,
    style,
  }).format(number);
};

// ref: https://stackoverflow.com/a/3866442
export const setCaretToEndOfContentEditable = (contentEditableElement) => {
  let range, selection;

  if (document.createRange) {
    //Firefox, Chrome, Opera, Safari, IE 9+
    range = document.createRange(); //Create a range (a range is a like the selection but invisible)

    range.selectNodeContents(contentEditableElement); //Select the entire contents of the element with the range

    range.collapse(false); //collapse the range to the end point. false means collapse to end rather than the start

    selection = window.getSelection(); //get the selection object (allows you to change selection)

    selection.removeAllRanges(); //remove any selections already made

    selection.addRange(range); //make the range you have just created the visible selection
  } else if (document.selection) {
    //IE 8 and lower
    range = document.body.createTextRange(); //Create a range (a range is a like the selection but invisible)

    range.moveToElementText(contentEditableElement); //Select the entire contents of the element with the range

    range.collapse(false); //collapse the range to the end point. false means collapse to end rather than the start

    range.select(); //Select the range (make it the visible selection
  }
};

export const getProperty = (object, path) => {
  path = path?.replace(/\[(\w+)\]/g, ".$1"); // convert indexes to properties

  path = path?.replace(/^\./, ""); // strip a leading dot

  let keys = path?.split(".");

  for (let i = 0, n = keys.length; i < n; ++i) {
    let key = keys[i];

    if (key in object) {
      object = object[key];
    } else {
      return;
    }
  }

  return object;
};

/**
 * Parse a localized number to a float.
 * @param {string} stringNumber - the localized number
 * @param {string} locale - [optional] the locale that the number is represented in. Omit this parameter to use the current locale.
 */
export const parseLocaleNumber = (stringNumber, locale) => {
  const thousandSeparator = Intl.NumberFormat(locale)
    .format(11111)
    .replace(/\p{Number}/gu, "");
  const decimalSeparator = Intl.NumberFormat(locale)
    .format(1.1)
    .replace(/\p{Number}/gu, "");

  return parseFloat(
    stringNumber
      .replace(new RegExp("\\" + thousandSeparator, "g"), "")
      .replace(new RegExp("\\" + decimalSeparator), "."),
  );
};

export const setProperty = (object, path, value) => {
  const parts = path.split(".");

  const limit = parts.length - 1;

  for (let i = 0; i < limit; ++i) {
    const key = parts[i];

    object = object[key] ?? (object[key] = {});
  }

  const key = parts[limit];

  object[key] = value;
};

export const timeAgoByDay = (date) => {
  if (isSameDate(date, new Date(), "date")) {
    return formatDate(date, "hh:mm A");
  }

  if (getDateDiff(new Date(), date, "date") < 2) {
    return "Yesterday";
  }

  return formatDate(date, "DD/MM/YY");
};

export const getUserNameOrEmail = (user) => {
  return user?.name ?? user?.email ?? "-";
};

export const upperFirstFunc = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const capitalizePreservingSpecialChars = (str) => {
  return str.replace(/\b\w+/g, function (word) {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
};

export const generateId = () => 100000 + Math.floor(Math.random() * 900000);

export const sheetToBuffer = (sheet) => {
  const buffer = new ArrayBuffer(sheet.length);
  const view = new Uint8Array(buffer);

  for (let i = 0; i != sheet.length; ++i) view[i] = sheet.charCodeAt(i) & 0xff;

  return buffer;
};

export const getFileName = (fileName) => {
  const currentDate = new Date();
  const formattedDate = formatDate(currentDate, "YYYYDDMM");
  const time = formatDate(currentDate, "HHMMSS");

  const tempFileName = `${formattedDate + time + "-" + fileName}`;

  return tempFileName;
};

export const getStartEndDateBy = ({
  date = new Date(),
  by,
  weekStartsOn = 0,
} = {}) => {
  const dateStartEndMap = {
    week: {
      gte: startOfWeek(date, { weekStartsOn }),
      lte: endOfWeek(date, { weekStartsOn }),
    },
    month: {
      gte: startOfMonth(date),
      lte: endOfMonth(date),
    },
    year: {
      gte: startOfYear(date),
      lte: endOfYear(date),
    },
  };

  return dateStartEndMap[by];
};

export const omitKeys = (obj, keys) => {
  if (!obj || typeof obj !== "object") return {};

  const result = {};

  Object.keys(obj).forEach((key) => {
    if (!keys.includes(key)) {
      result[key] = obj[key];
    }
  });

  return result;
};

export const getValue = (obj, path) => {
  if (Array.isArray(path)) {
    let sum = 0;

    for (const p of path) {
      const val = getProperty(obj, p);

      if (val !== undefined && val !== null) {
        sum += +val;
      }
    }

    return sum;
  }

  const value = getProperty(obj, path);

  return value;
};

export const humanizeIdentifier = (identifier) => {
  if (!identifier) return "";

  return identifier
    .replaceAll("_", " ")
    .replace(/\bv\d+\b/gi, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Creates a periodic fetcher that calls a function at regular intervals
 * @param {Function} fetchFn - The function to call periodically
 * @param {Object} options - Configuration options
 * @param {number} options.interval - Interval in milliseconds (default: 30000)
 * @param {boolean} options.immediate - Whether to fetch immediately on start (default: true)
 * @returns {Promise<number>} The interval ID
 */
export const createPeriodicFetcher = async (
  fetchFn,
  { interval = 30000, immediate = true } = {},
) => {
  const shouldFetch = () => {
    if (!offline) return true;

    return !unref(offline);
  };

  // Fetch immediately if requested
  if (immediate) {
    try {
      if (shouldFetch()) {
        await fetchFn();
      }
    } catch (error) {
      console.error(error);
    }
  }

  // Then poll at the specified interval
  return setInterval(async () => {
    try {
      if (shouldFetch()) {
        await fetchFn();
      }
    } catch (error) {
      console.error(error);
    }
  }, interval);
};

/**
 * Extracts two-letter initials from a name string
 * @param {string} fullName - Full name string
 * @returns {string} Two-letter uppercase initials
 */
const extractInitialsFromName = (fullName) => {
  const words = fullName.split(" ");

  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }

  return fullName.substring(0, 2).toUpperCase();
};

/**
 * Gets initials from a contact object
 * @param {Object} contact - Contact object with firstName, lastName, businessName
 * @returns {string} Two-letter uppercase initials or "UN"
 */
const getInitialsFromContact = (contact) => {
  const firstName = contact.firstName || "";
  const lastName = contact.lastName || "";

  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }

  if (firstName) {
    return extractInitialsFromName(firstName);
  }

  if (contact.businessName) {
    return extractInitialsFromName(contact.businessName);
  }

  return "UN";
};

/**
 * Gets initials from a name string
 * @param {string} name - Name string
 * @param {boolean} capitalize - Whether to capitalize the name
 * @returns {string} Two-letter uppercase initials or "UN"
 */
const getInitialsFromString = (name, capitalize) => {
  let processedName = name;

  if (capitalize) {
    processedName = useChangeCase(name, "capitalCase").value;
  }

  const isPhoneNumber = /^[+0-9\s]+$/.test(processedName);

  if (isPhoneNumber) {
    return "UN";
  }

  const nameComponents = processedName?.split(" ").filter(Boolean) || [];

  if (nameComponents.length === 0) {
    return "UN";
  }

  const firstInitial = nameComponents[0]?.[0] || "U";
  const lastInitial =
    nameComponents.length > 1
      ? nameComponents[nameComponents.length - 1]?.[0] || ""
      : "";

  return `${firstInitial}${lastInitial}`.toUpperCase();
};

/**
 * Generates avatar initials from a name or contact object
 * @param {string|Object} nameOrContact - The name string or contact object
 * @param {Object} options - Configuration options
 * @param {boolean} options.capitalize - Whether to capitalize the name (default: false)
 * @returns {string} Two-letter uppercase initials (e.g., "JD" for "John Doe")
 *
 * Priority logic:
 * 1. If phone number detected -> returns "UN"
 * 2. If contact object with firstName + lastName -> first initial of each
 * 3. If contact object with firstName only (multi-word) -> first two word initials
 * 4. If contact object with firstName only (single word) -> first two characters
 * 5. If contact object with businessName -> similar to firstName logic
 * 6. If string with multiple words -> first + last word initials
 * 7. Fallback -> "UN"
 */
export const getAvatarInitials = (
  nameOrContact,
  { capitalize = false } = {},
) => {
  // Handle null/undefined
  if (!nameOrContact) return "UN";

  // Check if it's a contact object
  if (typeof nameOrContact === "object") {
    return getInitialsFromContact(nameOrContact);
  }

  return getInitialsFromString(nameOrContact, capitalize);
};

export const sortByDate = (firstEntity, secondEntity) => {
  return new Date(firstEntity.date) - new Date(secondEntity.date);
};

/**
 * Performs deep equality comparison between two values of any type
 * Handles primitives, objects, arrays, dates, null, and undefined values
 * @param {*} firstValue - The first value to compare
 * @param {*} secondValue - The second value to compare
 * @returns {boolean} - True if values are deeply equal, false otherwise
 */
export function deepEqual(firstValue, secondValue) {
  // Fast path: identical references or primitive equality
  if (firstValue === secondValue) return true;

  // Handle Date objects by comparing timestamps
  if (firstValue instanceof Date && secondValue instanceof Date) {
    return firstValue.getTime() === secondValue.getTime();
  }

  // Handle cases where either value is falsy or non-object
  if (
    !firstValue ||
    !secondValue ||
    (typeof firstValue !== "object" && typeof secondValue !== "object")
  ) {
    return firstValue === secondValue;
  }

  // Handle explicit null/undefined cases (after falsy check above)
  if (
    firstValue === null ||
    firstValue === undefined ||
    secondValue === null ||
    secondValue === undefined
  ) {
    return false;
  }

  // Ensure objects have the same prototype
  if (firstValue?.prototype !== secondValue?.prototype) return false;

  const firstValueKeys = Object.keys(firstValue);

  // Quick length check before deep comparison
  if (firstValueKeys.length !== Object.keys(secondValue).length) {
    return false;
  }

  // Recursively compare all properties
  return firstValueKeys.every((propertyKey) =>
    deepEqual(firstValue[propertyKey], secondValue[propertyKey]),
  );
}

//global search related helpers

const SPECIAL_NUMBER_TYPES = new Set(["purchase_order", "debit_note", "bill"]);

export const getDisplayText = (item, query) => {
  const fallback = String(
    item.internal_number ||
      item.number ||
      item.shop_name ||
      item.name ||
      item.title ||
      item._id,
  );

  if (!SPECIAL_NUMBER_TYPES.has(item.type)) return fallback;

  const q = query.toLowerCase();

  if (item.number && String(item.number).toLowerCase().includes(q))
    return String(item.number);

  if (
    item.internal_number &&
    String(item.internal_number).toLowerCase().includes(q)
  )
    return String(item.internal_number);

  return fallback;
};

export const filterUserDocs = (documents) =>
  documents.filter((item) => {
    const hasShopName = !!item.shop_name?.trim();
    const hasName = !!item.name?.trim();

    return item.role === "buyer" || item.role === "seller"
      ? hasShopName || hasName
      : hasName;
  });

export const getCategoryLabel = (type) => {
  return TYPE_LABEL_MAP[type] ?? useChangeCase(type, "capitalCase").value;
};

export const buildGroupHeader = (
  type,
  categoryLabel,
  filteredDocs,
  returnedCount,
  totalDocs,
) => ({
  label: categoryLabel === "E-Way Bill" ? "E-Way Bills" : categoryLabel,
  count: type === "user" ? filteredDocs.length : returnedCount,
  shownCount: type === "user" ? filteredDocs.length : totalDocs.length,
  isGroup: true,
  categoryKey: type,
});

export const isSpecialDisplayType = (categoryKey) =>
  SPECIAL_NUMBER_TYPES.has(categoryKey);
